import { useCallback, useEffect, useState } from 'react'
import { type ArtifactErrorKind, isArtifactError } from './artifacts'

/**
 * The one async-value shape in the app.
 *
 * Every load - the national GeoJSON, a site payload, `meta.json`, the
 * methodology document - reports through this, so "is it still loading" means
 * the same thing in the map, the filter panel and the detail rail, and the
 * skeleton / error / empty decisions get made the same way in all three.
 *
 * Four states rather than three: `idle` is what "nothing is selected yet" looks
 * like, and it is not the same as "loading", which is what the skeleton is for.
 * Collapsing the two makes an empty panel flash a skeleton on first paint.
 */

/** Why a load failed, in a form the UI can turn into localized copy. */
export interface LoadFailure {
  readonly kind: ArtifactErrorKind | 'unknown'
  /** Which artifact, e.g. `status.json`. `null` when the failure is not a fetch. */
  readonly source: string | null
  /** Validator output, when the contract is what failed. */
  readonly detail: string | null
  readonly notFound: boolean
  /** Developer-facing text. Shown as small print, never as the headline. */
  readonly message: string
}

/**
 * Carries a {@link LoadFailure} back across a boundary that cannot carry an
 * `Error`.
 *
 * The stations worker validates off the main thread and reports failures as
 * plain data, because the structured clone algorithm would flatten an
 * `ArtifactError` to a bare message and lose the `kind` the UI writes its copy
 * from. This is how that data becomes a rejection again.
 */
export class LoadFailureError extends Error {
  readonly failure: LoadFailure

  constructor(failure: LoadFailure) {
    super(failure.message)
    this.name = 'LoadFailureError'
    this.failure = failure
  }
}

export const toLoadFailure = (error: unknown): LoadFailure => {
  if (error instanceof LoadFailureError) return error.failure
  if (isArtifactError(error))
    return {
      kind: error.kind,
      source: error.source,
      detail: error.detail ?? null,
      notFound: error.notFound,
      message: error.message,
    }

  return {
    kind: 'unknown',
    source: null,
    detail: null,
    notFound: false,
    message: error instanceof Error ? error.message : String(error),
  }
}

export type Resource<T> =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'error'; readonly failure: LoadFailure }

const IDLE = { status: 'idle' } as const
const LOADING = { status: 'loading' } as const

const RETRY_SEPARATOR = ':'

/**
 * Run `load` whenever `key` changes, dropping the result of any load a newer
 * key has already superseded.
 *
 * `key === null` means "nothing to load" and parks the resource at `idle`.
 *
 * `load` must be a stable reference - a module-level function, not an inline
 * closure - because the effect depends on it. Every caller here passes one, and
 * the alternative (a ref, or a lint suppression) hides a refetch-per-render bug
 * rather than preventing it.
 *
 * The retry counter rides in the effect's key rather than sitting beside it as
 * an unused dependency: `2:eipa-1000` is a different request from `1:eipa-1000`
 * even though it is the same site.
 */
export const useResource = <T>(
  key: string | null,
  load: (key: string) => Promise<T>,
): { readonly resource: Resource<T>; readonly reload: () => void } => {
  const [resource, setResource] = useState<Resource<T>>(key === null ? IDLE : LOADING)
  const [attempt, setAttempt] = useState(0)
  const request = key === null ? null : `${attempt}${RETRY_SEPARATOR}${key}`

  const reload = useCallback(() => setAttempt((value) => value + 1), [])

  useEffect(() => {
    if (request === null) {
      setResource(IDLE)
      return
    }

    // The reads themselves are memoised and shared, so they are not cancelled -
    // a second panel wanting the same site should get the in-flight promise, not
    // a hole. What is cancelled is this component's interest in the answer.
    let current = true
    setResource(LOADING)

    load(request.slice(request.indexOf(RETRY_SEPARATOR) + 1))
      .then((value) => {
        if (current) setResource({ status: 'ready', value })
      })
      .catch((error: unknown) => {
        if (current) setResource({ status: 'error', failure: toLoadFailure(error) })
      })

    return () => {
      current = false
    }
  }, [request, load])

  return { resource, reload }
}
